import { desktopApi, mailSubmitMessage } from './desktopApi';
import { labelConflicts, threeWayMerge } from './draftMerge.mjs';
import { chooseDraftAction } from './draftResume.mjs';
import { ownershipFromConflict, syncFailureAction, transferredDraftDecision } from './draftTransfer.mjs';
import { clearAgentPhotos, deleteLocalDraft, listLocalDrafts, readLocalDraft, writeLocalDraft } from './offlineStore';
import { uploadQueuedPhotos, uploadTaggedPhotos } from './reportPhotoSync';

const listeners = new Set();
const snapshots = new Map();
let started = false;
let flushing = false;

const MESSAGES = {
  idle: 'Prêt',
  syncing: 'Synchronisation…',
  synced: 'Synchronisé',
  pending: 'En attente de synchronisation',
  offline: 'Hors ligne — brouillon sur cet appareil',
  conflict: 'Conflit : votre saisie est conservée, à vérifier',
  error: 'Échec de synchronisation',
  submitted: 'Rapport envoyé',
};

function browserOnline() {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

function publish(agentId, patch) {
  const prev = snapshots.get(agentId) || {
    status: browserOnline() ? 'idle' : 'offline',
    message: browserOnline() ? MESSAGES.idle : MESSAGES.offline,
    conflictKeys: [],
    conflictLabels: [],
    online: browserOnline(),
    updatedAt: null,
  };
  const next = { ...prev, online: browserOnline(), ...patch };
  if (!next.message) next.message = MESSAGES[next.status] || MESSAGES.idle;
  snapshots.set(agentId, next);
  listeners.forEach((listener) => listener(agentId, next));
  return next;
}

export function getSyncSnapshot(agentId) {
  return snapshots.get(agentId) || {
    status: browserOnline() ? 'idle' : 'offline',
    message: browserOnline() ? MESSAGES.idle : MESSAGES.offline,
    conflictKeys: [],
    conflictLabels: [],
    online: browserOnline(),
    updatedAt: null,
  };
}

export function subscribeSync(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function networkError(error) {
  return !error?.response;
}

async function pushDraft(agentId, data, expectedUpdatedAt, conflicts) {
  const result = await desktopApi.saveReportDraft(agentId, {
    agent_id: agentId,
    data,
    expected_updated_at: expectedUpdatedAt || '',
    force: conflicts.length > 0,
  });
  const previous = await readLocalDraft(agentId).catch(() => null);
  const record = {
    agentId,
    data,
    baseData: data,
    serverUpdatedAt: result?.updated_at || null,
    localUpdatedAt: new Date().toISOString(),
    pending: false,
    pendingSubmit: false,
    conflictKeys: conflicts,
    ui: previous?.ui || null,
  };
  await writeLocalDraft(record);
  const labels = labelConflicts(conflicts);
  publish(agentId, {
    status: conflicts.length ? 'conflict' : 'synced',
    message: conflicts.length
      ? `Conflit fusionné (${labels.join(', ')}). Votre saisie est gardée.`
      : MESSAGES.synced,
    conflictKeys: conflicts,
    conflictLabels: labels,
    updatedAt: record.serverUpdatedAt,
  });
  return { ok: true, data, conflictKeys: conflicts, conflictLabels: labels, message: getSyncSnapshot(agentId).message };
}

function clearSessionDraftUi(agentId) {
  if (!agentId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(`vatelsec-draft-ui:${agentId}`);
  } catch (error) {
    void error;
  }
}

/**
 * Le rapport de la copie locale a été transféré à un collègue ?
 * Seule une réponse explicite du serveur efface cette copie-là (celle de cet agent).
 * Erreur réseau ou refus : on garde tout.
 */
export async function reconcileTransferredDraft(agentId, confirmed = null) {
  if (!agentId) return null;
  const local = await readLocalDraft(agentId).catch(() => null);
  const reportId = String(local?.data?.id || '');
  if (!reportId || local?.submittedReportId) return null;
  let ownership = confirmed;
  if (!ownership) {
    try {
      ownership = await desktopApi.getDraftOwnership(agentId, reportId);
    } catch (error) {
      return null;
    }
  }
  const decision = transferredDraftDecision(local, ownership);
  if (!decision.clear) return null;
  const current = await readLocalDraft(agentId).catch(() => null);
  if (String(current?.data?.id || '') !== decision.reportId) return null;
  await deleteLocalDraft(agentId);
  clearSessionDraftUi(agentId);
  publish(agentId, {
    status: 'idle',
    message: MESSAGES.idle,
    conflictKeys: [],
    conflictLabels: [],
    transferredReportId: decision.reportId,
    transferredMessage: decision.message,
  });
  return decision;
}

export async function syncAgent(agentId) {
  if (!agentId) return { ok: false, message: MESSAGES.error };
  const local = await readLocalDraft(agentId);
  if (!local) {
    publish(agentId, { status: browserOnline() ? 'idle' : 'offline', message: browserOnline() ? MESSAGES.idle : MESSAGES.offline, conflictKeys: [], conflictLabels: [] });
    return { ok: true, message: MESSAGES.idle };
  }
  if (!browserOnline()) {
    publish(agentId, { status: 'offline', message: local.pendingSubmit ? 'Hors ligne — le rapport partira au retour du réseau' : MESSAGES.offline, conflictKeys: local.conflictKeys || [], conflictLabels: labelConflicts(local.conflictKeys) });
    return { ok: true, offline: true, data: local.data, message: MESSAGES.offline, conflictKeys: local.conflictKeys || [] };
  }
  if (!local.pending && !local.pendingSubmit) {
    publish(agentId, { status: local.conflictKeys?.length ? 'conflict' : 'synced', conflictKeys: local.conflictKeys || [], conflictLabels: labelConflicts(local.conflictKeys), message: local.conflictKeys?.length ? MESSAGES.conflict : MESSAGES.synced });
    return { ok: true, data: local.data, message: MESSAGES.synced, conflictKeys: local.conflictKeys || [] };
  }

  publish(agentId, { status: 'syncing', message: MESSAGES.syncing });
  try {
    await uploadTaggedPhotos(agentId);
    if (local.pendingSubmit) {
      let reportId = local.submittedReportId || '';
      let mailState = null;
      if (!reportId) {
        const created = await desktopApi.createReport(local.data);
        reportId = created?.id || '';
        mailState = created?.mail || null;
        await writeLocalDraft({ ...local, submittedReportId: reportId, pending: false, pendingSubmit: true });
      }
      if (reportId) await uploadQueuedPhotos(agentId, reportId);
      await desktopApi.deleteReportDraft(agentId).catch(() => null);
      await deleteLocalDraft(agentId);
      const submittedMessage = mailState ? mailSubmitMessage(mailState) : MESSAGES.submitted;
      publish(agentId, { status: 'synced', message: submittedMessage, conflictKeys: [], conflictLabels: [] });
      return { ok: true, submitted: true, message: submittedMessage, conflictKeys: [] };
    }

    let remote = null;
    try {
      remote = await desktopApi.getReportDraft(agentId);
    } catch (error) {
      if (networkError(error)) {
        publish(agentId, { status: 'offline', message: MESSAGES.offline });
        return { ok: true, offline: true, data: local.data, message: MESSAGES.offline };
      }
      throw error;
    }

    const serverStamp = remote?.exists ? remote.updated_at : '';
    const known = local.serverUpdatedAt || '';
    if (!remote?.exists && known) {
      // Le serveur avait ce brouillon et ne l'a plus : transféré depuis un autre appareil ?
      const moved = await reconcileTransferredDraft(agentId);
      if (moved) return { ok: true, transferred: true, message: moved.message, conflictKeys: [] };
    }
    if (remote?.exists && known && known !== serverStamp) {
      const { merged, conflicts } = threeWayMerge(local.baseData, local.data, remote.data);
      return pushDraft(agentId, merged, serverStamp, conflicts);
    }
    if (remote?.exists && !known) {
      const { merged, conflicts } = threeWayMerge(local.baseData, local.data, remote.data);
      return pushDraft(agentId, merged, serverStamp, conflicts);
    }

    return pushDraft(agentId, local.data, known, []);
  } catch (error) {
    const detail = error?.response?.data?.detail;
    const status = error?.response?.status;
    const next = syncFailureAction(status, detail);
    if (next !== 'keep') {
      // Rapport passé à un collègue (depuis un autre appareil) : on retire cette copie et
      // on arrête de la renvoyer. Le 409 « report_transferred » suffit ; sinon on demande.
      const moved = await reconcileTransferredDraft(agentId, ownershipFromConflict(detail));
      if (moved) return { ok: true, transferred: true, message: moved.message, conflictKeys: [] };
    }
    if (next === 'check-then-merge') {
      try {
        const { merged, conflicts } = threeWayMerge(local.baseData, local.data, detail.data);
        return pushDraft(agentId, merged, detail.updated_at || '', conflicts);
      } catch (retryError) {
        publish(agentId, { status: 'error', message: MESSAGES.error });
        return { ok: false, message: MESSAGES.error, error: retryError };
      }
    }
    if (networkError(error)) {
      publish(agentId, { status: 'offline', message: MESSAGES.offline });
      return { ok: true, offline: true, data: local.data, message: MESSAGES.offline };
    }
    publish(agentId, { status: 'error', message: MESSAGES.error });
    return { ok: false, message: MESSAGES.error, error };
  }
}

export async function rememberDraft(agentId, data, { submit = false, ui = null } = {}) {
  const existing = await readLocalDraft(agentId).catch(() => null);
  const record = {
    agentId,
    data,
    baseData: existing?.baseData || null,
    serverUpdatedAt: existing?.serverUpdatedAt || null,
    localUpdatedAt: new Date().toISOString(),
    pending: true,
    pendingSubmit: submit || !!existing?.pendingSubmit,
    conflictKeys: [],
    ui: ui || existing?.ui || null,
  };
  await writeLocalDraft(record);
  publish(agentId, { status: browserOnline() ? 'pending' : 'offline', message: browserOnline() ? MESSAGES.pending : MESSAGES.offline });
  const result = await syncAgent(agentId);
  return {
    ok: result.ok !== false,
    offline: !!result.offline,
    message: result.message,
    conflictKeys: result.conflictKeys || [],
    conflictLabels: result.conflictLabels || labelConflicts(result.conflictKeys),
    data: result.data || data,
    transferred: !!result.transferred,
    toast: result.transferred ? result.message : result.offline
      ? (submit ? 'Hors réseau. Le rapport partira dès le retour du signal.' : 'Brouillon enregistré sur cet appareil')
      : (submit
        ? (result.message || 'Rapport enregistré, envoi par e-mail en attente')
        : (result.conflictKeys?.length ? result.message : 'Brouillon synchronisé')),
  };
}

export async function loadBestDraft(agentId) {
  const moved = await reconcileTransferredDraft(agentId);
  if (moved) return { action: 'transferred', message: moved.message };
  let local = await readLocalDraft(agentId).catch(() => null);
  if (local?.pending || local?.pendingSubmit) {
    const result = await syncAgent(agentId);
    const fresh = await readLocalDraft(agentId).catch(() => local);
    if (result.submitted) return { action: 'submitted', message: result.message };
    if (result.transferred) return { action: 'transferred', message: result.message };
    if (result.offline || fresh?.pending || fresh?.pendingSubmit) {
      return {
        action: 'restore',
        data: fresh?.data || local.data,
        ui: fresh?.ui || local.ui || null,
        message: result.message || 'Brouillon de cet appareil',
        conflictKeys: result.conflictKeys || fresh?.conflictKeys || [],
        updatedAt: fresh?.serverUpdatedAt || null,
      };
    }
    local = fresh;
  }

  let remote = null;
  try {
    remote = await desktopApi.getReportDraft(agentId);
  } catch (error) {
    if (local?.data) {
      publish(agentId, { status: 'offline', message: MESSAGES.offline });
      return {
        action: 'restore',
        data: local.data,
        ui: local.ui || null,
        message: MESSAGES.offline,
        conflictKeys: local.conflictKeys || [],
      };
    }
    if (networkError(error)) return { action: 'none', offline: true };
    return { action: 'none' };
  }

  return chooseDraftAction(local, remote);
}

export async function patchDraftUi(agentId, ui) {
  if (!agentId || !ui) return;
  const existing = await readLocalDraft(agentId).catch(() => null);
  if (!existing?.data) return;
  if (JSON.stringify(existing.ui || null) === JSON.stringify(ui)) return;
  await writeLocalDraft({ ...existing, ui });
}

export async function adoptServerDraft(agentId, data, updatedAt, ui) {
  const existing = await readLocalDraft(agentId).catch(() => null);
  await writeLocalDraft({
    agentId,
    data,
    baseData: data,
    serverUpdatedAt: updatedAt || null,
    localUpdatedAt: new Date().toISOString(),
    pending: false,
    pendingSubmit: false,
    conflictKeys: [],
    ui: ui === undefined ? (existing?.ui || null) : ui,
  });
  publish(agentId, { status: 'synced', message: MESSAGES.synced, conflictKeys: [], conflictLabels: [] });
}

export async function discardDraft(agentId) {
  await deleteLocalDraft(agentId).catch(() => null);
  await clearAgentPhotos(agentId).catch(() => null);
  await desktopApi.deleteReportDraft(agentId).catch(() => null);
  publish(agentId, { status: 'idle', message: MESSAGES.idle, conflictKeys: [], conflictLabels: [] });
}

export async function resolveConflict(agentId, choice) {
  const local = await readLocalDraft(agentId);
  if (choice === 'server') {
    const remote = await desktopApi.getReportDraft(agentId);
    if (remote?.exists && remote.data) {
      await adoptServerDraft(agentId, remote.data, remote.updated_at);
      return { data: remote.data, message: 'Version du serveur reprise' };
    }
  }
  if (local?.data) {
    await writeLocalDraft({ ...local, conflictKeys: [], pending: false });
  }
  publish(agentId, { status: 'synced', message: 'Saisie du téléphone conservée', conflictKeys: [], conflictLabels: [] });
  return { data: local?.data || null, message: 'Saisie du téléphone conservée' };
}

export async function clearLocalAfterSubmit(agentId, message) {
  await deleteLocalDraft(agentId).catch(() => null);
  publish(agentId, { status: 'synced', message: message || MESSAGES.submitted, conflictKeys: [], conflictLabels: [] });
}

async function flushAll() {
  if (flushing || !browserOnline()) return;
  flushing = true;
  try {
    const rows = await listLocalDrafts();
    for (const row of rows) {
      if (row?.pending || row?.pendingSubmit) {
        await syncAgent(row.agentId);
      }
    }
  } catch (error) {
    console.error('Synchronisation impossible', error);
  } finally {
    flushing = false;
  }
}

export function startSyncEngine() {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('online', () => {
    snapshots.forEach((_, agentId) => publish(agentId, { online: true, status: 'pending', message: MESSAGES.pending }));
    flushAll();
  });
  window.addEventListener('offline', () => {
    snapshots.forEach((_, agentId) => publish(agentId, { online: false, status: 'offline', message: MESSAGES.offline }));
  });
  window.setInterval(flushAll, 20000);
  flushAll();
}
